/**
 * ADR-213 — draws the personal offer inside the cart's own breakdown.
 *
 * Reads the snapshot eros-offer.js publishes (`eros:offer-state`) and writes:
 *  - the «خصم العرض» line (countdown pill + amount or reason) into every
 *    [data-eros-offer-line] host (cart page totals, drawer footer, checkout);
 *  - the discounted total into every [data-eros-offer-total];
 *  - per offered line: the Eros chip, the crossed-out unit price and the new one.
 * Everything it changes is remembered and put back when the offer stops
 * applying. Cart sections are re-rendered by the theme; the last snapshot is
 * applied again after each re-render (cart-drawer:refreshed).
 */
(function (root) {
  'use strict';
  if (root.ErosOfferRender) return;

  var MINUS = '−';
  var URGENT_MS = 10 * 60 * 1000; /* app: OfferCountdownPill._urgent */
  var CLOCK =
    '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path></svg>';
  var originals = new WeakMap();
  var last = null;
  var strings = null;

  function S() {
    if (strings) return strings;
    var node = document.querySelector('script[data-eros-offer-strings]');
    try {
      strings = node ? JSON.parse(node.textContent) : {};
    } catch (e) {
      strings = {};
    }
    return strings;
  }

  function text(key, vars) {
    var out = String(S()[key] || '');
    Object.keys(vars || {}).forEach(function (k) {
      out = out.replace('{' + k + '}', vars[k]);
    });
    return out;
  }

  function money(iqd) {
    var ui = root.ErosUI;
    if (ui && ui.formatMoneyIqd) return ui.formatMoneyIqd(iqd);
    return String(Math.round(iqd)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function el(tag, cls, content) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (content !== undefined) n.textContent = content;
    return n;
  }

  function remember(node) {
    if (!originals.has(node)) originals.set(node, node.innerHTML);
  }

  function restore(node) {
    if (!originals.has(node)) return;
    node.innerHTML = originals.get(node);
    originals.delete(node);
  }

  /* ------------------------------------------------------ discount line */

  /* ADR-224: the delivery part is named, not added into the amount — the
     checkout's delivery row already shows the reduced fee. */
  function shippingText(shipping) {
    return shipping.kind === 'free' ? text('free_delivery') : text('delivery_minus', { amount: money(shipping.value) });
  }

  function amountText(view) {
    if (view.kind === 'applies') {
      var parts = [];
      if (view.amountIqd > 0 || !view.shipping) parts.push(MINUS + money(view.amountIqd));
      if (view.shipping) parts.push(shippingText(view.shipping));
      return parts.join(' · ');
    }
    if (view.kind === 'pending') return '…';
    if (view.kind === 'ended') return text('ended');
    return text(view.key);
  }

  function countdownLabel(expiresAt) {
    return root.ErosOffer.formatCountdown(expiresAt - Date.now());
  }

  function pill(expiresAt) {
    var p = el('span', 'eros-offer-pill');
    p.setAttribute('data-eros-offer-countdown', String(expiresAt));
    p.insertAdjacentHTML('afterbegin', CLOCK);
    var t = el('span', 'eros-offer-pill__time', countdownLabel(expiresAt));
    p.appendChild(t);
    p.setAttribute('title', text('ends_in', { time: t.textContent }));
    return p;
  }

  /** Fill one host (cart page, drawer or checkout sheet) with the discount line. */
  function renderLine(host, view) {
    host.replaceChildren();
    if (!view) {
      host.hidden = true;
      return;
    }
    host.hidden = false;
    host.className = 'eros-offer-line eros-offer-line--' + view.kind;
    var label = el('span', 'eros-offer-line__label', text('line_label'));
    host.appendChild(label);
    if (view.expiresAt) host.appendChild(pill(view.expiresAt));
    host.appendChild(el('span', 'eros-offer-line__amount', amountText(view)));
  }

  /**
   * The checkout sheet's «خصم العرض» row — the same line as the cart. `apply`
   * takes the quote's `offer`; `onChange` re-totals the sheet. At zero the row
   * says so and the discount drops.
   */
  function checkoutRow(onChange) {
    var row = el('div', 'eros-offer-line');
    row.hidden = true;
    var api = { row: row, discountIqd: 0, shipping: null };
    api.apply = function (offer) {
      api.discountIqd = offer.applies ? Math.round(Number(offer.discountIqd) || 0) : 0;
      api.shipping = offer.applies ? root.ErosOffer.parseShipping(offer.shipping) : null;
      renderLine(row, root.ErosOffer.viewFromOffer(offer));
      onChange();
    };
    document.addEventListener('eros:offer-ended', function () {
      if (!row.isConnected) return;
      api.discountIqd = 0;
      api.shipping = null;
      renderLine(row, { kind: 'ended' });
      onChange();
    }, { once: true });
    return api;
  }

  /* ------------------------------------------------------------- totals */

  function applyTotal(node, discountIqd) {
    var target = node.querySelector('[data-eros-offer-values]') || node;
    if (!(discountIqd > 0)) {
      restore(target);
      return;
    }
    var base = Number(node.getAttribute('data-base-iqd'));
    if (!isFinite(base)) return;
    var compare = Math.max(base, Number(node.getAttribute('data-compare-iqd')) || 0);
    var next = money(Math.max(0, base - discountIqd));
    remember(target);
    var kind = node.getAttribute('data-eros-offer-total');
    if (kind === 'button') {
      target.textContent = next;
      return;
    }
    target.replaceChildren();
    var page = kind === 'page';
    target.appendChild(el('span', page ? 'text-lg text-on-sale' : 'eros-cart-price__sale', next));
    target.appendChild(el('span', page ? 'text-subdued line-through text-sm' : 'eros-cart-price__compare', money(compare)));
  }

  function applyLineTotal(node, lineDiscountIqd) {
    if (!(lineDiscountIqd > 0)) {
      restore(node);
      return;
    }
    var base = Number(node.getAttribute('data-base-iqd'));
    if (!isFinite(base)) return;
    remember(node);
    node.textContent = money(Math.max(0, base - lineDiscountIqd));
  }

  /* -------------------------------------------------------- cart lines */

  function chipText(chip) {
    return chip.kind === 'percent' ? MINUS + chip.value + '%' : MINUS + money(chip.value);
  }

  function clearChip(item) {
    item.querySelectorAll('[data-eros-offer-chip]').forEach(function (c) {
      c.remove();
    });
  }

  function applyItem(item, view) {
    var prices = item.querySelector('price-list');
    var caption = item.querySelector('.eros-cart-line-qty-caption');
    clearChip(item);
    if (!view) {
      if (prices) restore(prices);
      if (caption) restore(caption);
      return;
    }
    if (prices) {
      var sale = prices.querySelector('sale-price');
      var compare = prices.querySelector('compare-at-price');
      var saleClass = ((sale && sale.className) || '').replace('text-subdued', 'text-on-sale');
      var compareClass = (compare && compare.className) || 'text-subdued line-through';
      remember(prices);
      /* ADR-213 §2.6: the crossed-out price is the product's original price, not its sale price */
      prices.replaceChildren(
        el('sale-price', saleClass || 'text-on-sale', money(view.discountedUnitIqd)),
        el('compare-at-price', compareClass, money(Math.max(view.unitIqd, Number(item.getAttribute('data-compare-at-iqd')) || 0)))
      );
      var chip = el('span', 'eros-offer-chip', chipText(view.chip));
      chip.setAttribute('data-eros-offer-chip', '');
      prices.insertAdjacentElement('afterend', chip);
    }
    var qty = Number(item.getAttribute('data-quantity'));
    if (caption && qty > 0 && qty === view.discountedQuantity) {
      remember(caption);
      caption.textContent = qty + ' × ' + money(view.discountedUnitIqd);
    } else if (caption) {
      restore(caption);
    }
  }

  /* -------------------------------------------------------------- apply */

  /* The cart's own sale savings next to a line host (compare-at total − price total). */
  function saleSavings(host) {
    var t = host.parentElement && host.parentElement.querySelector('[data-eros-offer-total][data-compare-iqd]');
    if (!t) return 0;
    return Math.max(0, (Number(t.getAttribute('data-compare-iqd')) || 0) - (Number(t.getAttribute('data-base-iqd')) || 0));
  }

  function apply(snap) {
    last = snap;
    /* Owner 2026-10-07: in the cart the line shows everything the customer saves
       (product sales + the personal discount); the checkout sheet keeps the offer only. */
    document.querySelectorAll('[data-eros-offer-line]').forEach(function (host) {
      var v = snap.view;
      renderLine(host, v && v.kind === 'applies' ? Object.assign({}, v, { amountIqd: v.amountIqd + saleSavings(host) }) : v);
    });
    document.querySelectorAll('[data-eros-offer-total]').forEach(function (node) {
      applyTotal(node, snap.discountIqd);
    });
    var byVariant = {};
    snap.lines.forEach(function (l) {
      byVariant[l.variantId] = l;
    });
    document.querySelectorAll('line-item[data-variant-id]').forEach(function (item) {
      applyItem(item, byVariant[item.getAttribute('data-variant-id')] || null);
    });
    document.querySelectorAll('[data-eros-offer-line-total]').forEach(function (node) {
      var v = byVariant[node.getAttribute('data-variant-id')];
      applyLineTotal(node, v ? v.lineDiscountIqd : 0);
    });
  }

  /* ---------------------------------------------------------- countdown */

  function tick() {
    var offer = root.ErosOffer;
    if (!offer) return;
    var now = Date.now();
    var ended = false;
    document.querySelectorAll('[data-eros-offer-countdown]').forEach(function (p) {
      var exp = Number(p.getAttribute('data-eros-offer-countdown'));
      if (exp <= now) {
        ended = true;
        return;
      }
      var label = offer.formatCountdown(exp - now);
      p.classList.toggle('eros-offer-pill--urgent', exp - now < URGENT_MS);
      var t = p.querySelector('.eros-offer-pill__time');
      if (t && t.textContent !== label) {
        t.textContent = label;
        p.setAttribute('title', text('ends_in', { time: label }));
      }
    });
    if (ended) offer.expire();
  }

  document.addEventListener('eros:offer-state', function (e) {
    apply(e.detail);
  });
  /* the theme swapped the drawer's DOM: draw the same answer on the new nodes */
  document.addEventListener('cart-drawer:refreshed', function () {
    if (last) apply(last);
  });
  setInterval(tick, 1000);

  /* An offer link landed: show the cart drawer (its content refreshes on cart:refresh). */
  function openDrawer() {
    if (!root.customElements) return;
    root.customElements.whenDefined('cart-drawer').then(function () {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.show === 'function') drawer.show();
    });
  }

  /* ADR-332: the promotions' quote discount on the same total hosts (no offer token in the cart). */
  function applyTotals(discountIqd) {
    document.querySelectorAll('[data-eros-offer-total]').forEach(function (node) { applyTotal(node, discountIqd); });
  }

  root.ErosOfferRender = { renderLine: renderLine, checkoutRow: checkoutRow, openDrawer: openDrawer, text: text, applyTotals: applyTotals };
})(window);
